import { useTranslation } from 'react-i18next';
import React, { useCallback, useMemo } from 'react';
import NoUsersWithAccess from '../../../../assets/images/ill_empty_access_sharing_operators.svg';
import { Input } from '@progress/kendo-react-inputs';
import { ic_close_small_black as BlackCloseIcon } from 'app/widgets/SVGs';
import SearchIcon from 'assets/images/ic_search.svg';
import { removeEmojis } from 'utils/general';
import { useIsMobile } from 'app/hooks/useIsMobile';
import useGetEquipmentData from 'app/hooks/useGetEquipmentData';
import SharedWithRow from './SharedWithRow';
import OperatorDetailsDialog from './OperatorDetailsDialog';
import { IOperatorDetails, ISharedUser } from 'types/IEquipmentEntry';
import { SharedItemShareType } from 'services/api/shares';
import { groupSharedUsers, sortByLabel } from 'utils/shares';
import { filterSharedWith } from 'utils/searchFilter';
import { useDebounce } from 'app/hooks/useDebounce';
import { NoResultFound } from 'app/components/access-sharing';
import './SharedWith.less';
import { useGetUserInfoQuery } from 'app/hooks/user-info';

function SharedWith() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const { equipments, sharesData } = useGetEquipmentData();
  const { data: user } = useGetUserInfoQuery();

  const sharedUsers = useMemo(() => {
    if (!equipments || !sharesData || !user) return [];
    return groupSharedUsers(equipments, user);
  }, [equipments, sharesData]);

  const [searchValue, setSearchValue] = React.useState<string>('');
  const debouncedSearchTerm = useDebounce(searchValue, 300);

  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isOperatorDetailsDialogOpen, setIsOperatorDetailsDialogOpen] =
    React.useState(false);

  const [selectedOperator, setSelectedOperator] =
    React.useState<ISharedUser | null>(null);

  const getSharedItemsDetails = useCallback(
    (operator: ISharedUser) => {
      if (!equipments || !sharesData) return [];

      const sharedItems = [
        ...operator.sharedFleets.sort(sortByLabel),
        ...operator.sharedEquipments.sort(sortByLabel),
      ];

      const details = sharedItems.map(item => {
        const { shareType } = item.sharedItemDetails;
        const sharedWith = item.sharedWith.find(
          shared => shared.email === operator.email,
        );

        if (!sharedWith) return undefined;

        switch (shareType) {
          case SharedItemShareType.EQUIPMENT: {
            const equipment = equipments?.find(
              equipment => equipment.id === item.sharedItemDetails.exposedId,
            );
            if (!equipment) return undefined;
            return {
              exposedId: item.sharedItemDetails.exposedId,
              exposedAccessSharingRecordId:
                sharedWith.exposedAccessSharingRecordId,
              type: shareType,
              role: sharedWith.sharedRole,
              startTime: sharedWith.startsAt,
              endTime: sharedWith.endsAt,
              sharedItemLabel: equipment.nickName || equipment.model,
              details: {
                model: equipment?.model,
                pinOrSerial: equipment?.pinOrSerial,
              },
            };
          }
          case SharedItemShareType.FLEET: {
            const fleet = sharesData?.fleets?.find(
              f =>
                f.sharedItemDetails.exposedId ===
                item.sharedItemDetails.exposedId,
            );
            if (!fleet) return undefined;
            return {
              exposedId: fleet.sharedItemDetails.exposedId,
              exposedAccessSharingRecordId:
                sharedWith.exposedAccessSharingRecordId,
              type: shareType,
              role: sharedWith.sharedRole,
              startTime: sharedWith.startsAt,
              endTime: sharedWith.endsAt,
              sharedItemLabel: fleet.sharedItemDetails.sharedItemLabel,
              details: {
                equipmentCount:
                  fleet.sharedItemDetails.exposedEquipmentIds?.length,
              },
            };
          }
          default:
            return undefined;
        }
      });

      return details.filter(Boolean) as IOperatorDetails[];
    },
    [equipments, sharesData],
  );

  const handleOperatorDetailsClick = (operator: ISharedUser) => {
    setSelectedOperator(operator);
    setIsOperatorDetailsDialogOpen(true);
  };

  const filteredSharedUsers = useMemo(() => {
    if (!sharedUsers) {
      return [];
    }
    return filterSharedWith(sharedUsers, debouncedSearchTerm);
  }, [sharedUsers, debouncedSearchTerm]);

  const hasAnySharedUsers = sharedUsers.length > 0;
  const hasAnyFilteredSharedUsers = filteredSharedUsers.length > 0;

  const selectedOperatorDetails = useMemo(() => {
    if (!selectedOperator) return undefined;
    const operator = filteredSharedUsers?.find(
      u => u.email === selectedOperator.email,
    );
    if (!operator) return undefined;
    return {
      displayName: operator.displayName,
      email: operator.email,
      sharedItems: getSharedItemsDetails(operator),
    };
  }, [selectedOperator, filteredSharedUsers, getSharedItemsDetails]);

  return (
    <div className="access-sharing-subcontainer shared-with">
      <div className="access-sharing-subcontainer-header">
        <div className="access-sharing-subcontainer-header-content no-filters">
          <h3>{t('shared_with')}</h3>

          {hasAnySharedUsers && (
            <div
              className="site-search open"
              style={{
                justifySelf: 'flex-end',
                maxWidth: '400px',
                width: '100%',
              }}
            >
              <span className="search-icon">
                <img alt="Search" src={SearchIcon}></img>
              </span>
              <div className="search-input">
                <Input
                  name="shared_with_users_search"
                  value={searchValue}
                  placeholder={t('shared_with_search_placeholder')}
                  onChange={newValue => {
                    setSearchValue(removeEmojis(newValue.value));
                  }}
                  onClick={() => setIsExpanded(!isExpanded)}
                />
                {searchValue && (
                  <span
                    className="search-icon-close"
                    onClick={() => {
                      setSearchValue('');
                    }}
                  >
                    <BlackCloseIcon />
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="access-sharing-subcontainer-body">
        {!hasAnySharedUsers ? (
          <div className="access-sharing-subcontainer-body-empty">
            <div className="access-sharing-body-picture">
              <img src={NoUsersWithAccess}></img>
            </div>
            <div className="access-sharing-body-text">
              <h4>{t('shared_with_no_users_title')}</h4>
              <p>{t('shared_with_no_users_text')}</p>
            </div>
          </div>
        ) : (
          <div className="access-sharing-subcontainer-body-table">
            <div
              className="access-sharing-subcontainer-body-table-header shared-with"
              style={{ padding: '16px 12px' }}
            >
              <div>{t('user')}</div>
              <div>{t('access_to')}</div>
              <div></div>
              <div></div>
            </div>

            {hasAnyFilteredSharedUsers ? (
              <div className="access-sharing-subcontainer-body-table-body shared-with">
                {filteredSharedUsers?.map(user => {
                  return (
                    <SharedWithRow
                      user={user}
                      isMobile={isMobile}
                      key={user.email}
                      onClick={() => handleOperatorDetailsClick(user)}
                    />
                  );
                })}
              </div>
            ) : (
              <NoResultFound
                className="shared-with__no-result-found"
                title={t('access_sharing.table.no_results.title')}
                description={t(
                  'access_sharing.table.no_results.try_different_keyword',
                )}
              />
            )}
          </div>
        )}
        {isOperatorDetailsDialogOpen && selectedOperatorDetails && (
          <OperatorDetailsDialog
            onClose={() => setIsOperatorDetailsDialogOpen(false)}
            operatorDetails={selectedOperatorDetails}
            isMobile={isMobile}
          />
        )}
      </div>
    </div>
  );
}

export default SharedWith;
